<script setup lang="ts">
    import { useNavigationStore } from '../../stores/navigation';
    import { __ } from '@/js/stores/expressions';
    import { useKeycloak } from '@/js/composables/useKeycloak';
    import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { Role, usePermissions } from '@/js/composables/usePermissions';
    import {
        PermissionArea,
        DashboardModulePermissionFlag,
        InteractionModulePermissionFlag,
        AskMeModulePermissionFlag,
        QualityAssuranceModulePermissionFlag,
    } from '@/js/consts/permissions';
    import NavigationItem from './NavigationItem.vue';
    import Logotype from '../Logotype.vue';
    import {
        ChevronDownIcon,
        ChevronUpIcon,
        CircleCheckIcon,
        CircleHelpIcon,
        ClipboardCheckIcon,
        ClipboardListIcon,
        DatabaseIcon,
        LayersIcon,
        LayoutGridIcon,
        LogOutIcon,
        MessageSquareIcon,
        PanelLeftCloseIcon,
        PanelLeftOpenIcon,
        SettingsIcon,
    } from '@lucide/vue';
    import UserAvatar from '../UserAvatar.vue';
    import EchoPopover from '../EchoPopover.vue';
    import NavigationSectionHeader from './NavigationSectionHeader.vue';
    import NavigationFeedbackItem from './NavigationFeedbackItem.vue';
    import EchoButton from '../EchoButton.vue';
    import IconButton from '../IconButton.vue';

    const keycloak = useKeycloak();
    const { hasRole, hasPermission } = usePermissions();

    const canViewDashboard = hasPermission(
        PermissionArea.DashboardModule,
        DashboardModulePermissionFlag.ViewDashboards
    );
    const canAccessInteractions = hasPermission(
        PermissionArea.InteractionModule,
        InteractionModulePermissionFlag.Access
    );
    const canAccessAskMe = hasPermission(
        PermissionArea.AskMeModule,
        AskMeModulePermissionFlag.Access
    );
    const canAccessQualityAssurance = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.Access
    );
    const canManualCoaching = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ManualCoaching
    );

    const logoutUrl = () => {
        return keycloak.createLogoutUrl({
            redirectUri: window.location.origin,
        });
    };

    const navigation = useNavigationStore();

    const route = useRoute();

    const userNavigationPopover = ref<InstanceType<typeof EchoPopover>>();

    watch(
        () => route.path,
        () => {
            userNavigationPopover.value?.close();
        }
    );

    const scrollContainer = ref<HTMLDivElement>();
    const canScrollUp = ref(false);
    const canScrollDown = ref(false);

    const updateScrollState = () => {
        const el = scrollContainer.value;
        if (!el) {
            canScrollUp.value = false;
            canScrollDown.value = false;
            return;
        }
        canScrollUp.value = el.scrollTop > 0;
        canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    };

    let resizeObserver: ResizeObserver | undefined;

    onMounted(() => {
        nextTick(updateScrollState);
        if (scrollContainer.value) {
            resizeObserver = new ResizeObserver(updateScrollState);
            resizeObserver.observe(scrollContainer.value);
            for (const child of Array.from(scrollContainer.value.children)) {
                resizeObserver.observe(child);
            }
        }
    });

    onBeforeUnmount(() => {
        resizeObserver?.disconnect();
    });

    watch(
        () => navigation.visible,
        () => nextTick(updateScrollState)
    );

    const scrollBy = (delta: number) => {
        scrollContainer.value?.scrollBy({ top: delta, behavior: 'smooth' });
    };

    const onFocusIn = (event: FocusEvent) => {
        const container = scrollContainer.value;
        const target = event.target as HTMLElement | null;
        if (!container || !target || !container.contains(target)) return;

        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const margin = 48; // leave room for fade/chevron overlay

        if (targetRect.top < containerRect.top + margin) {
            container.scrollBy({
                top: targetRect.top - containerRect.top - margin,
                behavior: 'smooth',
            });
        } else if (targetRect.bottom > containerRect.bottom - margin) {
            container.scrollBy({
                top: targetRect.bottom - containerRect.bottom + margin,
                behavior: 'smooth',
            });
        }
    };
</script>

<template>
    <div
        class="fixed z-50 h-screen flex flex-col shrink-0 bg-white border-gray-lightest text-body group"
        :class="navigation.visible ? 'w-72 border-r' : 'w-0 sm:border-r sm:w-16'"
        :aria-label="__('main-navigation')"
    >
        <div
            class="pt-6 border-b border-gray-lightest pb-6"
            :class="navigation.visible ? 'mx-6' : 'mx-3'"
        >
            <div class="pl-2">
                <Logotype height="h-6" :symbol-only="!navigation.visible" />
            </div>

            <button
                class="bg-white absolute w-9 h-9 flex items-center justify-center custom-focus rounded-sm cursor-pointer hover:bg-gray-lightest"
                :class="
                    navigation.visible
                        ? 'right-4 top-4.5'
                        : 'left-4 top-4.5 sm:left-auto sm:right-3 sm:opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 '
                "
                @click="navigation.toggle()"
            >
                <PanelLeftCloseIcon v-if="navigation.visible" class="size-7 sm:size-5" />
                <PanelLeftOpenIcon v-else class="size-7 sm:size-5" />
            </button>
        </div>
        <nav class="grow flex-col min-h-0" :class="navigation.visible ? 'flex' : 'hidden sm:flex'">
            <div class="relative grow flex flex-col min-h-0">
                <div
                    ref="scrollContainer"
                    class="grow flex flex-col min-h-0 overflow-y-auto hide-scrollbar"
                    @scroll="updateScrollState"
                    @focusin="onFocusIn"
                >
                    <div
                        v-show="canScrollUp"
                        class="pointer-events-none absolute inset-x-0 top-0 h-16 bg-linear-to-b from-white from-10% to-transparent flex items-start justify-center"
                    >
                        <IconButton
                            class="pointer-events-auto"
                            size="size-5"
                            area-size="w-full h-8"
                            :aria-label="__('scroll-up')"
                            @click="scrollBy(-120)"
                        >
                            <ChevronUpIcon class="size-5" />
                        </IconButton>
                    </div>
                    <div class="grow pb-6 pt-6" :class="navigation.visible ? 'px-6' : 'px-3'">
                        <ul class="flex flex-col gap-1">
                            <NavigationItem
                                v-if="canViewDashboard"
                                to="/workspace/dashboard"
                                :label="__('dashboard')"
                            >
                                <template #icon>
                                    <LayoutGridIcon class="size-5" />
                                </template>
                            </NavigationItem>
                            <NavigationItem
                                v-if="canAccessAskMe"
                                to="/ask-me"
                                :label="__('ask-me')"
                            >
                                <template #icon>
                                    <MessageSquareIcon class="size-5" />
                                </template>
                            </NavigationItem>
                            <NavigationItem
                                v-if="canAccessInteractions"
                                to="/interactions"
                                :label="__('interactions')"
                            >
                                <template #icon>
                                    <ClipboardListIcon class="size-5" />
                                </template>
                            </NavigationItem>
                        </ul>

                        <template v-if="canAccessQualityAssurance">
                            <NavigationSectionHeader>
                                {{ __('quality-assurance') }}
                            </NavigationSectionHeader>

                            <ul class="flex flex-col gap-1">
                                <NavigationItem
                                    v-if="canManualCoaching"
                                    to="/quality-assurance/coaching"
                                    :label="__('coaching')"
                                >
                                    <template #icon>
                                        <CircleCheckIcon class="size-5" />
                                    </template>
                                </NavigationItem>
                                <NavigationItem
                                    to="/quality-assurance/protocols"
                                    :label="__('protocols')"
                                >
                                    <template #icon>
                                        <ClipboardCheckIcon class="size-5" />
                                    </template>
                                </NavigationItem>
                                <NavigationItem
                                    to="/quality-assurance/organisational-results"
                                    :label="__('organisational-results')"
                                >
                                    <template #icon>
                                        <LayersIcon class="size-5" />
                                    </template>
                                </NavigationItem>
                            </ul>
                        </template>
                    </div>
                </div>
                <div
                    v-show="canScrollDown"
                    class="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-linear-to-t from-white from-10% to-transparent flex items-end justify-center"
                >
                    <IconButton
                        class="pointer-events-auto"
                        size="size-5"
                        area-size="w-full h-8"
                        :aria-label="__('scroll-down')"
                        @click="scrollBy(120)"
                    >
                        <ChevronDownIcon class="size-5" />
                    </IconButton>
                </div>
            </div>

            <div
                class="pt-3 pb-6 border-t border-gray-lightest"
                :class="navigation.visible ? 'mx-6' : 'mx-3'"
            >
                <ul class="flex flex-col gap-1">
                    <NavigationItem
                        href="https://help.indicateme.se/LoPuM3WX4XqEcWae3AZ7"
                        :label="__('navigation-support')"
                    >
                        <template #icon>
                            <CircleHelpIcon class="size-5" />
                        </template>
                    </NavigationItem>
                    <NavigationFeedbackItem />
                </ul>

                <ul class="mt-3">
                    <li>
                        <EchoPopover
                            ref="userNavigationPopover"
                            type="menu"
                            :position="navigation.visible ? 'top-start' : 'left-end'"
                        >
                            <template #popover>
                                <EchoButton variant="block" route="/settings">
                                    <template #icon-left><SettingsIcon class="size-5" /></template>
                                    {{ __('settings') }}
                                </EchoButton>
                                <EchoButton
                                    v-if="hasRole(Role.SYSOP)"
                                    variant="block"
                                    route="/back-office"
                                >
                                    <template #icon-left><DatabaseIcon class="size-5" /></template>
                                    {{ __('back-office') }}
                                </EchoButton>
                                <EchoButton variant="block" :href="logoutUrl()" target="_self">
                                    <template #icon-left><LogOutIcon class="size-5" /></template>
                                    {{ __('logout') }}
                                </EchoButton>
                            </template>
                            <template #button="{ anchor, togglePopover, buttonAttrs }">
                                <button
                                    :ref="anchor"
                                    class="flex gap-3 p-1.5 rounded-sm w-full items-center cursor-pointer custom-focus hover:bg-gray-lightest"
                                    v-bind="buttonAttrs"
                                    @click="togglePopover"
                                >
                                    <UserAvatar
                                        :label="$userInfo?.name || $userInfo?.email"
                                        size="sm"
                                        color="avocado"
                                    />
                                    <span v-if="navigation.visible">
                                        {{ $userInfo?.name || $userInfo?.email }}
                                    </span>
                                </button>
                            </template>
                        </EchoPopover>
                    </li>
                </ul>
            </div>
        </nav>
    </div>
</template>
